import { Injectable } from '@angular/core';
import { ApiService } from '@core/services/api.service';
import { StateService } from '@core/services/state.service';
import { BehaviorSubject, of, Observable, throwError } from 'rxjs';
import { switchMap, catchError, take, tap, map } from 'rxjs/operators';
import { Column, KoltsegKategoria } from '../models/torzsek';

@Injectable({
  providedIn: 'root',
})
export class BevetelKategoriakService {
  private readonly apiEndpoint = 'bevetelkategoria';
  private readonly columnsEndpoint = 'bevetelkategoria/columns';

  private readonly _data = new BehaviorSubject<KoltsegKategoria[]>(null);
  private readonly _selectedData = new BehaviorSubject<KoltsegKategoria>(null);

  public data$ = this._data.pipe(
    switchMap((state: KoltsegKategoria[]) =>
      state ? of(state) : this.fetchData()
    ),
    map((data: KoltsegKategoria[]) => {
      return data.sort((a, b) => a.megnevezes.localeCompare(b.megnevezes));
    })
  );

  constructor(private api: ApiService, private stateService: StateService) {}

  get columns$(): Observable<Column[]> {
    return this.getColumns();
  }

  get initData$(): Observable<KoltsegKategoria[]> {
    return this._data.pipe(
      take(1),
      switchMap((state: KoltsegKategoria[]) =>
        state ? of(state) : this.fetchData()
      ),
      map((data: KoltsegKategoria[]) => {
        return data.sort((a, b) => a.megnevezes.localeCompare(b.megnevezes));
      })
    );
  }

  add(data: KoltsegKategoria): Observable<KoltsegKategoria> {
    return this.api.post(this.apiEndpoint, data).pipe(
      tap((added: KoltsegKategoria) =>
        this.stateService.setStateOnAdd(added, this._data)
      ),
      catchError((err: any) => throwError(err))
    );
  }

  delete(id: number): Observable<KoltsegKategoria> {
    return this.api.delete(this.apiEndpoint, id).pipe(
      tap((deleted: KoltsegKategoria) =>
        this.stateService.setStateOnDelete(deleted, this._data)
      ),
      catchError((err: any) => throwError(err))
    );
  }

  getById(id: string): Observable<KoltsegKategoria> {
    return this.data$.pipe(
      take(1),
      map((state) => {
        // Find the data
        const data =
          state.find((item: KoltsegKategoria) => item.id === +id) || null;

        // Update the data
        this._selectedData.next(data);

        // Return the data
        return data;
      }),
      switchMap((data) => {
        if (id === 'new') {
          return of(null);
        }

        if (!data) {
          return throwError('Could not found data with id of ' + id + '!');
        }

        return of(data);
      })
    );
  }

  update(data: KoltsegKategoria): Observable<KoltsegKategoria> {
    return this.api.put(`${this.apiEndpoint}/${data.id}`, data).pipe(
      tap((updated: KoltsegKategoria) =>
        this.stateService.setStateOnUpdate(updated, this._data)
      ),
      catchError((err: any) => throwError(err))
    );
  }

  private fetchData(): Observable<KoltsegKategoria[]> {
    return this.api.get(this.apiEndpoint).pipe(
      tap((data: KoltsegKategoria[]) => this._data.next(data)),
      catchError((err: any) => {
        console.log(err);
        return of([]);
      })
    );
  }

  private getColumns(): Observable<Column[]> {
    return this.api.get(this.columnsEndpoint).pipe(
      catchError((err: any) => {
        console.log(err);
        return of([]);
      })
    );
  }
}
